import { Platform } from 'react-native';
import { getE2ETestRunContext } from './e2eTestRun';

const SIGNUP_ATTRIBUTION_KEY = 'paperzilla-signup-attribution-v1';
const COOKIE_CONSENT_NAME = 'paperzilla-cookie-consent';
const ATTRIBUTION_SCHEMA_VERSION = 2;

type ConsentState = 'accepted' | 'declined' | 'unknown';
type InferredChannel =
    | 'direct'
    | 'organic_search'
    | 'paid_search'
    | 'social'
    | 'paid_social'
    | 'email'
    | 'referral'
    | 'display'
    | 'affiliate'
    | 'other';

export interface SignupAttribution {
    schema_version: number;
    captured_at: string;
    landing_path: string | null;
    landing_url: string | null;
    referrer_host: string | null;
    referrer_url: string | null;
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    utm_content: string | null;
    utm_term: string | null;
    gclid: string | null;
    fbclid: string | null;
    msclkid: string | null;
    ttclid: string | null;
    inferred_source: string;
    inferred_channel: InferredChannel;
    cookie_consent: ConsentState;
    test_run?: boolean;
    test_run_id?: string;
    test_run_tag?: string;
}

const SEARCH_ENGINE_HOST_HINTS = [
    'google.',
    'bing.',
    'duckduckgo.',
    'search.yahoo.',
    'yandex.',
    'baidu.',
];

const SOCIAL_HOST_HINTS = [
    'x.com',
    'twitter.com',
    't.co',
    'linkedin.com',
    'facebook.com',
    'instagram.com',
    'reddit.com',
    'youtube.com',
    'tiktok.com',
];

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') {
        return null;
    }

    const cookie = document.cookie
        .split(';')
        .map((entry) => entry.trim())
        .find((entry) => entry.startsWith(`${name}=`));

    if (!cookie) {
        return null;
    }

    return decodeURIComponent(cookie.split('=').slice(1).join('='));
}

function getCookieConsentState(): ConsentState {
    const value = readCookie(COOKIE_CONSENT_NAME);

    if (value === 'true') {
        return 'accepted';
    }
    if (value === 'false') {
        return 'declined';
    }
    return 'unknown';
}

function normalizeText(value: string | null, maxLength = 120): string | null {
    if (!value) {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    return trimmed.slice(0, maxLength);
}

function parseHost(urlString: string | null): string | null {
    if (!urlString) {
        return null;
    }

    try {
        return normalizeText(new URL(urlString).hostname.toLowerCase(), 120);
    } catch {
        return null;
    }
}

function sanitizeUrl(urlString: string | null): string | null {
    if (!urlString) {
        return null;
    }

    try {
        const parsed = new URL(urlString);
        parsed.username = '';
        parsed.password = '';
        parsed.search = '';
        parsed.hash = '';
        return normalizeText(parsed.toString(), 500);
    } catch {
        return null;
    }
}

function matchesHostHints(host: string | null, hints: string[]): boolean {
    if (!host) {
        return false;
    }

    return hints.some((hint) => host === hint || host.endsWith(hint));
}

function inferChannel(params: {
    utmMedium: string | null;
    utmSource: string | null;
    referrerHost: string | null;
    hasSearchClickId: boolean;
    hasSocialClickId: boolean;
}): InferredChannel {
    const medium = params.utmMedium?.toLowerCase() ?? '';
    const source = params.utmSource?.toLowerCase() ?? '';

    if (medium === 'e2e' || medium === 'qa' || source === 'qa') {
        return 'other';
    }

    if (params.hasSocialClickId) {
        return 'paid_social';
    }
    if (params.hasSearchClickId) {
        return 'paid_search';
    }

    if (medium) {
        if (medium.includes('email') || source.includes('newsletter')) {
            return 'email';
        }
        if (medium.includes('social') || medium === 'sm') {
            return 'social';
        }
        if (medium.includes('cpc') || medium.includes('ppc') || medium.includes('paid')) {
            return 'paid_search';
        }
        if (medium.includes('display') || medium.includes('banner')) {
            return 'display';
        }
        if (medium.includes('affiliate')) {
            return 'affiliate';
        }
        if (medium.includes('referral')) {
            return 'referral';
        }
        if (medium.includes('organic')) {
            return 'organic_search';
        }
    }

    if (matchesHostHints(params.referrerHost, SEARCH_ENGINE_HOST_HINTS)) {
        return 'organic_search';
    }
    if (matchesHostHints(params.referrerHost, SOCIAL_HOST_HINTS)) {
        return 'social';
    }
    if (params.referrerHost) {
        return 'referral';
    }

    return 'direct';
}

function inferSource(utmSource: string | null, referrerHost: string | null): string {
    if (utmSource) {
        return utmSource.toLowerCase();
    }

    if (referrerHost) {
        return referrerHost;
    }

    return 'direct';
}

function safeParseStoredAttribution(): SignupAttribution | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const raw = window.sessionStorage.getItem(SIGNUP_ATTRIBUTION_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as Partial<SignupAttribution>;
        if (
            parsed &&
            parsed.schema_version === ATTRIBUTION_SCHEMA_VERSION &&
            typeof parsed.inferred_source === 'string' &&
            typeof parsed.inferred_channel === 'string'
        ) {
            return parsed as SignupAttribution;
        }
    } catch {
        // Ignore malformed storage values
    }

    return null;
}

function buildAttributionFromWindow(): SignupAttribution | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const currentUrl = new URL(window.location.href);
    const e2eContext = getE2ETestRunContext();

    let utmSource = normalizeText(currentUrl.searchParams.get('utm_source'));
    let utmMedium = normalizeText(currentUrl.searchParams.get('utm_medium'));
    let utmCampaign = normalizeText(currentUrl.searchParams.get('utm_campaign'));
    const utmContent = normalizeText(currentUrl.searchParams.get('utm_content'));
    const utmTerm = normalizeText(currentUrl.searchParams.get('utm_term'));

    if (e2eContext) {
        utmSource = utmSource || 'qa';
        utmMedium = utmMedium || 'e2e';
        utmCampaign = utmCampaign || normalizeText(`qa-${e2eContext.tag}`, 120);
    }

    const gclid = normalizeText(currentUrl.searchParams.get('gclid'));
    const fbclid = normalizeText(currentUrl.searchParams.get('fbclid'));
    const msclkid = normalizeText(currentUrl.searchParams.get('msclkid'));
    const ttclid = normalizeText(currentUrl.searchParams.get('ttclid'));

    const landingUrl = sanitizeUrl(currentUrl.toString());
    const referrerHost = parseHost(document.referrer || null);
    const referrerUrl = sanitizeUrl(document.referrer || null);

    const inferredChannel = inferChannel({
        utmMedium,
        utmSource,
        referrerHost,
        hasSearchClickId: Boolean(gclid || msclkid),
        hasSocialClickId: Boolean(fbclid || ttclid),
    });

    const attribution: SignupAttribution = {
        schema_version: ATTRIBUTION_SCHEMA_VERSION,
        captured_at: new Date().toISOString(),
        landing_path: normalizeText(currentUrl.pathname, 240),
        landing_url: landingUrl,
        referrer_host: referrerHost,
        referrer_url: referrerUrl,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_term: utmTerm,
        gclid,
        fbclid,
        msclkid,
        ttclid,
        inferred_source: inferSource(utmSource, referrerHost),
        inferred_channel: inferredChannel,
        cookie_consent: getCookieConsentState(),
    };

    if (e2eContext) {
        attribution.test_run = true;
        attribution.test_run_id = e2eContext.runId;
        attribution.test_run_tag = e2eContext.tag;
    }

    return attribution;
}

function storeAttribution(attribution: SignupAttribution): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.setItem(SIGNUP_ATTRIBUTION_KEY, JSON.stringify(attribution));
    } catch {
        // Ignore storage failures
    }
}

export function getOrCreateSignupAttribution(): SignupAttribution | null {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return null;
    }

    const existing = safeParseStoredAttribution();
    if (existing) {
        return existing;
    }

    const attribution = buildAttributionFromWindow();
    if (!attribution) {
        return null;
    }

    storeAttribution(attribution);
    return attribution;
}
